import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import {
  getCities,
  getDevelopers,
  getAmenities,
} from "../../../services/api";
import "./PropertyForm.css";

const SUB_TYPES = [
  "Apartment",
  "Villa",
  "Row House",
  "Penthouse",
  "Studio",
  "Office Space",
  "Retail Shop",
  "Showroom",
  "Warehouse",
  "Residential Plot",
  "Commercial Plot",
  "Farm Land",
];

const UNIT_TYPES = [
  "1 BHK",
  "2 BHK",
  "3 BHK",
  "4 BHK",
  "5 BHK",
  "Villa",
  "Penthouse",
  "Studio",
  "Office",
  "Shop",
  "Plot",
];

const STATUSES = ["Draft", "Published", "Archived"];

const CATEGORIES = [
  { value: "Residential", label: "Residential" },
  { value: "Commercial", label: "Commercial" },
  { value: "Plots", label: "Plots" },
];

const STEPS = [
  { id: 1, title: "Basic Info", desc: "Title, city, developer" },
  { id: 2, title: "Media", desc: "Project images" },
  { id: 3, title: "Units & Floor Plans", desc: "Unit types & pricing" },
  { id: 4, title: "Content", desc: "Description & highlights" },
  { id: 5, title: "Amenities", desc: "Select amenities" },
  { id: 6, title: "SEO & Publish", desc: "Status & visibility" },
];

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const emptyUnit = () => ({
  uid: uid(),
  unitType: "2 BHK",
  area: "",
  price: "",
  floorPlan: "",
  floorPlanName: "",
  floorPlanFile: null,
});

const emptyLandmark = () => ({
  uid: uid(),
  name: "",
  distance: "",
  travelTime: "",
});

const emptyScore = () => ({
  uid: uid(),
  category: "",
  score: 70,
});
function toStableImageUrl(url) {
  if (!url || typeof url !== "string") return "";
  try {
    const u = new URL(url);
    return `${u.origin}${decodeURIComponent(u.pathname)}`;
  } catch {
    // fallback: cut at first ?
    const clean = url.split("?")[0];
    try {
      return decodeURIComponent(clean);
    } catch {
      return clean;
    }
  }
}

const empty = {
  title: "",
  Price: "",
  city: "",
  developer: "",
  locality: "",
  country: "India",
  address: "",
  category: "Residential",
  subType: "Apartment",
  rera: "",
  possessionStatus: "",
  galleryItems: [], // { kind: 'existing'|'new', url?, preview, file? }
  removedImages: [],
  units: [emptyUnit()],
  description: "",
  keyFeatures: [""],
  locationAdvantages: [""],
  landmarks: [emptyLandmark()],
  lifestyleScores: [emptyScore()],
  disclaimer: "",
  amenities: [],
  status: "Draft",
  featured: false,
  exclusive: false,
  luxurious: false,
};

function mergeInitial(initial) {
  if (!initial) {
    return {
      ...empty,
      units: [emptyUnit()],
      galleryItems: [],
      removedImages: [],
    };
  }

  const urls =
    initial.images ??
    initial.gallery ??
    (initial.cover || initial.image
      ? [initial.cover || initial.image]
      : []);

  const galleryItems = (urls || [])
    .filter((u) => typeof u === "string" && u.trim())
    .map((url) => {
      const stable = toStableImageUrl(url.trim());
      return {
        kind: "existing",
        url: stable,          // what we send back to API
        preview: url.trim(),  // full presigned URL for display now
      };
    });

  return {
    ...empty,
    ...initial,
    title: (initial.title ?? initial.name ?? "").toString().trim(),
    Price: (initial.Price ?? initial.price ?? "").toString().trim(),
    city: (initial.city ?? "").toString().trim(),
    developer: (initial.developer ?? initial.builder ?? "").toString().trim(),
    locality: (initial.locality ?? initial.location ?? "").toString().trim(),
    country: (initial.country ?? "India").toString().trim(),
    address: (initial.address ?? "").toString().trim(),
    category: (initial.category ?? "Residential").toString().trim(),
    subType: initial.subType ?? "Apartment",
    rera: (initial.rera ?? "").toString().trim(),
    possessionStatus: (
      initial.possessionStatus ??
      initial.possession ??
      ""
    )
      .toString()
      .trim(),
    description: (initial.description ?? "").toString().trim(),
    disclaimer: (initial.disclaimer ?? "").toString().trim(),
    galleryItems,
    removedImages: [],
    units: (initial.units?.length ? initial.units : [emptyUnit()]).map(
      (u) => ({
        uid: u.uid ?? uid(),
        unitType: u.unitType || "2 BHK",
        area: u.area ?? "",
        price: u.price ?? "",
        floorPlan: u.floorPlan || "",
        floorPlanName: u.floorPlanName || "",
        floorPlanFile: null,
      })
    ),
    keyFeatures:
      initial.keyFeatures?.length > 0
        ? initial.keyFeatures
        : initial.highlights?.length > 0
          ? initial.highlights
          : [""],
    locationAdvantages:
      initial.locationAdvantages?.length > 0
        ? initial.locationAdvantages
        : [""],
    landmarks: (
      initial.landmarks?.length
        ? initial.landmarks
        : initial.nearby?.length
          ? initial.nearby.map((n) => ({
            uid: uid(),
            name: n.name || "",
            distance: n.distance || "",
            travelTime: "",
          }))
          : [emptyLandmark()]
    ).map((l) => ({
      uid: l.uid ?? uid(),
      name: l.name || "",
      distance: l.distance || "",
      travelTime: l.travelTime || "",
    })),
    lifestyleScores: (
      initial.lifestyleScores?.length
        ? initial.lifestyleScores
        : [emptyScore()]
    ).map((l) => ({
      uid: l.uid ?? uid(),
      category: l.category || "",
      score: Number(l.score) || 70,
    })),
    amenities: Array.isArray(initial.amenities) ? initial.amenities : [],
    status:
      (initial.status ??
        (initial.active === false ? "Draft" : "Published"))
        .toString()
        .trim() || "Draft",
    featured: !!initial.featured,
    exclusive: !!initial.exclusive,
    luxurious: !!initial.luxurious,
  };
}

export default function PropertyForm({ initial, onSubmit, submitting }) {
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(() => mergeInitial(initial));

  const [cityOptions, setCityOptions] = useState([]);
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [amenityList, setAmenityList] = useState([]);
  const [mastersLoading, setMastersLoading] = useState(true);


  const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
  const ALLOWED_IMAGE_EXT = [".jpg", ".jpeg", ".png"];
  useEffect(() => {
    let cancelled = false;
    setMastersLoading(true);

    Promise.all([
      getCities().catch(() => []),
      getDevelopers().catch(() => []),
      getAmenities().catch(() => []),
    ]).then(([cities, developers, amenities]) => {
      if (cancelled) return;
      setCityOptions(cities || []);
      setDeveloperOptions(developers || []);
      setAmenityList(amenities || []);
      setMastersLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (initial) setForm(mergeInitial(initial));
  }, [initial]);

  const progress = useMemo(
    () => Math.round(((step - 1) / (STEPS.length - 1)) * 100),
    [step]
  );

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function setListItem(key, uidOrIdx, patch) {
    setForm((f) => ({
      ...f,
      [key]: f[key].map((it, i) =>
        (typeof uidOrIdx === "number" ? i === uidOrIdx : it.uid === uidOrIdx)
          ? typeof it === "string"
            ? patch
            : { ...it, ...patch }
          : it
      ),
    }));
  }

  function addTo(key, item) {
    setForm((f) => ({ ...f, [key]: [...f[key], item] }));
  }

  function removeFrom(key, uidOrIdx) {
    setForm((f) => ({
      ...f,
      [key]: f[key].filter((it, i) =>
        typeof uidOrIdx === "number" ? i !== uidOrIdx : it.uid !== uidOrIdx
      ),
    }));
  }

  function isAllowedImageFile(file) {
    const typeOk = ALLOWED_IMAGE_TYPES.includes(file.type?.toLowerCase());
    const name = (file.name || "").toLowerCase();
    const extOk = ALLOWED_IMAGE_EXT.some((ext) => name.endsWith(ext));
    return typeOk || extOk;
  }

  function onImages(e) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;

    const rejected = [];
    const accepted = [];

    files.forEach((file) => {
      if (!isAllowedImageFile(file)) {
        rejected.push(`${file.name}: only JPG or PNG allowed`);
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        rejected.push(`${file.name}: max size is 10MB`);
        return;
      }
      accepted.push(file);
    });

    if (rejected.length) {
      setErrors((prev) => ({
        ...prev,
        images: rejected.join(" · "),
      }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.images;
        return next;
      });
    }

    if (!accepted.length) return;

    const previews = accepted.map((file) => ({
      kind: "new",
      preview: URL.createObjectURL(file),
      file,
    }));

    setForm((f) => ({
      ...f,
      galleryItems: [...(f.galleryItems || []), ...previews],
    }));
  }

  function removeImage(index) {
    setForm((f) => {
      const galleryItems = [...(f.galleryItems || [])];
      const removed = galleryItems[index];
      galleryItems.splice(index, 1);

      // revoke blob URL
      if (removed?.kind === "new" && removed.preview?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(removed.preview);
        } catch {
          /* ignore */
        }
      }

      // track S3 URLs user removed (for backend cleanup)
      const removedImages = [...(f.removedImages || [])];
      if (removed?.kind === "existing" && removed.url) {
        removedImages.push(removed.url);
      }

      return {
        ...f,
        galleryItems,
        removedImages,
      };
    });
  }

  function onFloorPlan(e, unitUid) {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setListItem("units", unitUid, {
      floorPlan: preview,
      floorPlanName: file.name,
      floorPlanFile: file,
    });
    e.target.value = "";
  }

  function validateBasics() {
    const map = {};
    if (!form.title.trim()) map.title = "Title is required";
    if (!String(form.Price).trim()) map.Price = "Price is required";
    if (!form.city) map.city = "City is required";
    if (!form.developer.trim()) map.developer = "Developer is required";
    if (!form.category) map.category = "Category is required";
    setErrors(map);
    return Object.keys(map).length === 0;
  }

  function next(e) {
    e?.preventDefault?.();
    if (step === 1 && !validateBasics()) return;
    setStep((s) => Math.min(STEPS.length, s + 1));
  }

  function prev(e) {
    e?.preventDefault?.();
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  }

  function submit(e) {
    e.preventDefault();
    if (step !== STEPS.length) return;
    if (!validateBasics()) {
      setStep(1);
      return;
    }

    const galleryItems = form.galleryItems || [];

    // URLs still selected (existing S3 images user did NOT remove)
    const existingImages = galleryItems
      .filter((g) => g.kind === "existing" && g.url)
      .map((g) => toStableImageUrl(g.url));

    const removedImages = (form.removedImages || []).map(toStableImageUrl);
    // Only new File uploads
    const imageFiles = galleryItems
      .filter((g) => g.kind === "new" && g.file instanceof File)
      .map((g) => g.file);


    // Units: only attach File when user selected a new floor plan
    const units = (form.units || [])
      .filter((u) => u.unitType)
      .map((u) => {
        const hasNewFile = u.floorPlanFile instanceof File;

        return {
          unitType: u.unitType,
          area: Number(u.area) || 0,
          price: Number(u.price) || 0,
          // existing S3 floor plan (stable, no query)
          floorPlan:
            !hasNewFile &&
              typeof u.floorPlan === "string" &&
              u.floorPlan.startsWith("http")
              ? toStableImageUrl(u.floorPlan)
              : undefined,
          // only for NEW file mapping on backend
          floorPlanIndex: hasNewFile ? undefined : -1,
          floorPlanFile: hasNewFile ? u.floorPlanFile : null,
        };
      });

    onSubmit({
      ...form,
      keyFeatures: form.keyFeatures.filter((s) => String(s).trim()),
      locationAdvantages: form.locationAdvantages.filter((s) =>
        String(s).trim()
      ),
      landmarks: form.landmarks.filter((l) => l.name?.trim()),
      lifestyleScores: form.lifestyleScores.filter((l) =>
        l.category?.trim()
      ),
      units,
      existingImages, // remaining S3 URLs only
      imageFiles, // new files only
      removedImages: form.removedImages || [], // deleted S3 URLs
    });
  }

  return (
    <form onSubmit={submit} className="pf">
      {/* Stepper */}
      <div className="pf-stepper">
        <ol className="pf-steps">
          {STEPS.map((s) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => (s.id < step ? setStep(s.id) : null)}
                  className={`pf-step ${active ? "is-active" : ""} ${done ? "is-done" : ""
                    }`}
                >
                  <span className="pf-step-badge">
                    {done ? <Check className="h-3.5 w-3.5" /> : s.id}
                  </span>
                  <span className="pf-step-text">
                    <span className="pf-step-title">{s.title}</span>
                    <span className="pf-step-desc">{s.desc}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <div className="pf-progress">
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="pf-panel">
        {/* STEP 1 */}
        {step === 1 && (
          <div className="pf-grid-2">
            <Field
              label="Title"
              value={form.title}
              onChange={(v) => set("title", v)}
              error={errors.title}
            />

            <Select
              label="City"
              value={form.city}
              onChange={(v) => set("city", v)}
              error={errors.city}
              options={[
                {
                  value: "",
                  label: mastersLoading ? "Loading cities…" : "Select city",
                },
                ...cityOptions.map((c) => ({
                  value: c.name,
                  label: c.name,
                })),
              ]}
            />

            <Select
              label="Developer"
              value={form.developer}
              onChange={(v) => set("developer", v)}
              error={errors.developer}
              options={[
                {
                  value: "",
                  label: mastersLoading
                    ? "Loading developers…"
                    : "Select developer",
                },
                ...developerOptions.map((d) => ({
                  value: d.name,
                  label: d.name,
                })),
              ]}
            />

            <Field
              label="Locality"
              value={form.locality}
              onChange={(v) => set("locality", v)}
              placeholder="Hinjewadi"
            />

            <Field
              label="Country"
              value={form.country}
              onChange={(v) => set("country", v)}
            />

            <Field
              label="Price"
              value={form.Price}
              onChange={(v) => set("Price", v)}
              error={errors.Price}
              placeholder="50000000"
            />

            <div className="pf-span-2">
              <label className="pf-label">Address</label>
              <textarea
                rows={3}
                value={form.address}
                onChange={(e) => set("address", e.target.value)}
                className="pf-input"
              />
            </div>

            <Select
              label="Category"
              value={form.category}
              onChange={(v) => set("category", v)}
              error={errors.category}
              options={CATEGORIES}
            />

            <Select
              label="Sub Type"
              value={form.subType}
              onChange={(v) => set("subType", v)}
              options={SUB_TYPES.map((s) => ({ value: s, label: s }))}
            />

            <Field
              label="RERA Number"
              value={form.rera}
              onChange={(v) => set("rera", v)}
            />

            <Field
              label="Possession Status"
              value={form.possessionStatus}
              onChange={(v) => set("possessionStatus", v)}
              placeholder="Ready to move / Dec 2027"
            />
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="pf-stack">
            <div>
              <label className="pf-label">Project images (multiple)</label>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
                multiple
                onChange={onImages}
                className="pf-file"
              />
              <p className="pf-hint">
                JPG or PNG only · max 10MB each · first image is the cover.
              </p>
              {errors.images && <p className="pf-err">{errors.images}</p>}
            </div>

            <div className="pf-gallery">
              {(form.galleryItems || []).map((item, i) => (
                <div key={`${item.preview}-${i}`} className="pf-thumb">
                  <img src={item.preview} alt={`Project ${i + 1}`} />
                  {i === 0 && <span className="pf-cover-tag">Cover</span>}
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="pf-thumb-x"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {(form.galleryItems || []).length === 0 && (
                <p className="pf-empty">No images uploaded yet.</p>
              )}
            </div>
          </div>
        )}
        {/* STEP 3 */}
        {step === 3 && (
          <div className="pf-stack">
            {form.units.map((u) => (
              <div key={u.uid} className="pf-repeat">
                <div className="pf-grid-4">
                  <Select
                    label="Unit Type"
                    value={u.unitType}
                    onChange={(v) =>
                      setListItem("units", u.uid, { unitType: v })
                    }
                    options={UNIT_TYPES.map((t) => ({
                      value: t,
                      label: t,
                    }))}
                  />
                  <Field
                    label="Super Built-up Area (Sq. Ft.)"
                    type="number"
                    value={u.area}
                    onChange={(v) =>
                      setListItem("units", u.uid, { area: v })
                    }
                  />
                  <Field
                    label="Price (₹)"
                    type="number"
                    value={u.price}
                    onChange={(v) =>
                      setListItem("units", u.uid, { price: v })
                    }
                  />
                  <div>
                    <label className="pf-label">Floor Plan (Image/PDF)</label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => onFloorPlan(e, u.uid)}
                      className="pf-file"
                    />
                    {u.floorPlanName && (
                      <p className="pf-hint">{u.floorPlanName}</p>
                    )}
                    {u.floorPlan &&
                      typeof u.floorPlan === "string" &&
                      (u.floorPlan.startsWith("blob:") ||
                        u.floorPlan.startsWith("http")) && (
                        <div className="pf-fp-preview">
                          <img src={u.floorPlan} alt="Floor plan" />
                        </div>
                      )}
                  </div>
                </div>
                <button
                  type="button"
                  className="pf-remove"
                  onClick={() => removeFrom("units", u.uid)}
                >
                  <Trash2 className="h-4 w-4" /> Remove Unit
                </button>
              </div>
            ))}
            <button
              type="button"
              className="pf-add"
              onClick={() => addTo("units", emptyUnit())}
            >
              <Plus className="h-4 w-4" /> Add Unit
            </button>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div className="pf-stack-lg">
            <div>
              <label className="pf-label">Description</label>
              <textarea
                rows={6}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                className="pf-input"
              />
            </div>

            <Repeater
              title="Key Features"
              items={form.keyFeatures}
              onAdd={() => addTo("keyFeatures", "")}
              addLabel="Add Key Feature"
              render={(item, i) => (
                <input
                  className="pf-input"
                  value={item}
                  placeholder="e.g. 3-side open apartments"
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      keyFeatures: f.keyFeatures.map((x, j) =>
                        j === i ? e.target.value : x
                      ),
                    }))
                  }
                />
              )}
              onRemove={(i) => removeFrom("keyFeatures", i)}
            />

            <Repeater
              title="Location Advantages"
              items={form.locationAdvantages}
              onAdd={() => addTo("locationAdvantages", "")}
              addLabel="Add Location Advantage"
              render={(item, i) => (
                <input
                  className="pf-input"
                  value={item}
                  placeholder="e.g. 5 min from metro station"
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      locationAdvantages: f.locationAdvantages.map(
                        (x, j) => (j === i ? e.target.value : x)
                      ),
                    }))
                  }
                />
              )}
              onRemove={(i) => removeFrom("locationAdvantages", i)}
            />

            <Repeater
              title="Landmarks Around the Project"
              items={form.landmarks}
              onAdd={() => addTo("landmarks", emptyLandmark())}
              addLabel="Add Landmark"
              render={(item) => (
                <div className="pf-grid-3 pf-grow">
                  <input
                    className="pf-input"
                    placeholder="Landmark name"
                    value={item.name}
                    onChange={(e) =>
                      setListItem("landmarks", item.uid, {
                        name: e.target.value,
                      })
                    }
                  />
                  <input
                    className="pf-input"
                    placeholder="Distance (e.g. 1.2 km)"
                    value={item.distance}
                    onChange={(e) =>
                      setListItem("landmarks", item.uid, {
                        distance: e.target.value,
                      })
                    }
                  />
                  <input
                    className="pf-input"
                    placeholder="Travel time (optional)"
                    value={item.travelTime}
                    onChange={(e) =>
                      setListItem("landmarks", item.uid, {
                        travelTime: e.target.value,
                      })
                    }
                  />
                </div>
              )}
              onRemove={(i, item) => removeFrom("landmarks", item.uid)}
            />

            <Repeater
              title="Lifestyle Scores"
              items={form.lifestyleScores}
              onAdd={() => addTo("lifestyleScores", emptyScore())}
              addLabel="Add Score"
              render={(item) => (
                <div className="pf-grid-2 pf-grow">
                  <input
                    className="pf-input"
                    placeholder="Category (Connectivity, Safety…)"
                    value={item.category}
                    onChange={(e) =>
                      setListItem("lifestyleScores", item.uid, {
                        category: e.target.value,
                      })
                    }
                  />
                  <div className="pf-score">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={item.score}
                      onChange={(e) =>
                        setListItem("lifestyleScores", item.uid, {
                          score: Number(e.target.value),
                        })
                      }
                    />
                    <span>{item.score}</span>
                  </div>
                </div>
              )}
              onRemove={(i, item) =>
                removeFrom("lifestyleScores", item.uid)
              }
            />

            <div>
              <label className="pf-label">Disclaimer</label>
              <textarea
                rows={4}
                value={form.disclaimer}
                onChange={(e) => set("disclaimer", e.target.value)}
                className="pf-input"
              />
            </div>
          </div>
        )}

        {/* STEP 5 */}
        {step === 5 && (
          <div>
            <p className="pf-hint mb-3">
              Select all amenities available at this project.
            </p>
            {mastersLoading && (
              <p className="pf-empty">Loading amenities…</p>
            )}
            {!mastersLoading && amenityList.length === 0 && (
              <p className="pf-empty">
                No amenities found. Add them under Master Data → Amenities.
              </p>
            )}
            <div className="pf-amenities">
              {amenityList.map((a) => {
                const checked = form.amenities.includes(a.name);
                return (
                  <label
                    key={a.id}
                    className={`pf-amenity ${checked ? "is-on" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        set(
                          "amenities",
                          e.target.checked
                            ? [...form.amenities, a.name]
                            : form.amenities.filter((x) => x !== a.name)
                        )
                      }
                    />
                    {a.name}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 6 */}
        {step === 6 && (
          <div className="pf-stack-lg">
            <div className="pf-grid-2">
              <Select
                label="Property Status"
                value={form.status}
                onChange={(v) => set("status", v)}
                options={STATUSES.map((s) => ({ value: s, label: s }))}
              />
            </div>
            <div className="pf-grid-3">
              <Toggle
                label="Featured Property"
                hint="Shows in Featured Projects"
                checked={form.featured}
                onChange={(v) => set("featured", v)}
              />
              <Toggle
                label="Exclusive Property"
                hint="Shows in Exclusive Projects"
                checked={form.exclusive}
                onChange={(v) => set("exclusive", v)}
              />
              <Toggle
                label="Most Luxurious"
                hint="Shows in Luxury Projects"
                checked={form.luxurious}
                onChange={(v) => set("luxurious", v)}
              />
            </div>
            <div className="pf-review">
              <div className="pf-review-title">Review</div>
              <div className="pf-review-grid">
                <Review k="Title" v={form.title} />
                <Review k="Price" v={form.Price} />
                <Review k="City" v={form.city} />
                <Review k="Developer" v={form.developer} />
                <Review k="Locality" v={form.locality} />
                <Review k="Category" v={form.category} />
                <Review k="Sub Type" v={form.subType} />
                <Review k="RERA" v={form.rera} />
                <Review k="Possession" v={form.possessionStatus} />
                <Review k="Images" v={`${(form.galleryItems || []).length}`} />
                <Review k="Units" v={`${form.units.length}`} />
                <Review k="Amenities" v={`${form.amenities.length}`} />
                <Review k="Status" v={form.status} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="pf-nav">
        <button
          type="button"
          onClick={prev}
          disabled={step === 1}
          className="pf-btn-ghost"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {step < STEPS.length ? (
          <button type="button" onClick={next} className="pf-btn-dark">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={submitting}
            className="pf-btn-gold"
          >
            {submitting ? "Saving…" : "Save Property"}{" "}
            <Check className="h-4 w-4" />
          </button>
        )}
      </div>
    </form>
  );
}

/* ── helpers ── */
function Repeater({ title, items, render, onAdd, onRemove, addLabel }) {
  return (
    <div className="pf-repeater">
      <div className="pf-repeater-head">{title}</div>
      <div className="pf-stack">
        {items.map((item, i) => (
          <div key={item.uid ?? i} className="pf-repeater-row">
            {render(item, i)}
            <button
              type="button"
              className="pf-row-x"
              onClick={() => onRemove(i, item)}
              aria-label="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="pf-empty">Nothing added yet.</p>
        )}
      </div>
      <button type="button" className="pf-add" onClick={onAdd}>
        <Plus className="h-4 w-4" /> {addLabel}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", error, placeholder }) {
  return (
    <div>
      <label className="pf-label">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="pf-input"
      />
      {error && <p className="pf-err">{error}</p>}
    </div>
  );
}

function Select({ label, value, onChange, options, error }) {
  return (
    <div>
      <label className="pf-label">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pf-input"
      >
        {options.map((o) => (
          <option key={`${o.value}-${o.label}`} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error && <p className="pf-err">{error}</p>}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange }) {
  return (
    <label className={`pf-toggle ${checked ? "is-on" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="pf-toggle-label">{label}</span>
        <span className="pf-toggle-hint">{hint}</span>
      </span>
    </label>
  );
}

function Review({ k, v }) {
  return (
    <div className="pf-review-row">
      <span className="pf-muted">{k}</span>
      <span className="pf-review-val">{v || "—"}</span>
    </div>
  );
}